import { injectable } from 'inversify';

import {
  FamilyAssessmentStatus,
  FamilyDegreeOfNeurologicalFunction,
  FamilyTreatmentPlanStatus,
  Gender,
} from '@shared/models/family';
import BaseService from '@core/services/base';
import Axios, { AxiosInstance } from 'axios';
import { ListResponse, PageInfo } from '@shared/types/services';
import { getQueries } from '@shared/utils/common';
import { SortingType } from '@shared/constants/services';
import qs from 'qs';
import { ChildItem, ChildProfile, IAddChild } from '@shared/models/patients';
import container from '@core/di';
import HttpClient from '@core/HttpClient';

export enum PatientsRequestFilters {
  Countries = 'Countries',
  Genders = 'Genders',
  AssessmentStatuses = 'AssessmentStatuses',
  TreatmentPlanStatuses = 'TreatmentPlanStatuses',
  DegreeOfNeurologicalFunctions = 'DegreeOfNeurologicalFunctions',
  AssignedClinicians = 'AssignedClinicians',
}

export interface GetPatientsParams {
  Page?: number;
  PageSize?: number;
  SearchKeyword?: string;
  [PatientsRequestFilters.Countries]?: number[];
  [PatientsRequestFilters.Genders]?: Gender[];
  [PatientsRequestFilters.AssessmentStatuses]?: FamilyAssessmentStatus[];
  [PatientsRequestFilters.TreatmentPlanStatuses]?: FamilyTreatmentPlanStatus[];
  [PatientsRequestFilters.DegreeOfNeurologicalFunctions]?: FamilyDegreeOfNeurologicalFunction[];
  [PatientsRequestFilters.AssignedClinicians]?: number[];
  'Sort.Field'?: PatientsSortingField;
  'Sort.Type'?: SortingType;
}

export enum PatientsSortingField {
  Created = 'Created',
  ClientFirstName = 'ClientFirstName',
  ChildName = 'ChildName',
  Clinician = 'Clinician',
}

@injectable()
export default class PatientsService extends BaseService {
  static diToken = Symbol('patients-service');
  private httpClient: AxiosInstance;

  constructor() {
    super({ domainArea: 'children' });
    this.httpClient = container.get<HttpClient>(HttpClient.diToken).getInstance();
  }

  async getPatientsList(
    params?: GetPatientsParams,
  ): Promise<{ pageInfo: PageInfo; items: ChildItem[] }> {
    const { data } = await Axios.get<ListResponse<ChildItem>>(`${this.urlPrefix}`, {
      params: params ?? this.getPatientListParams(),
      paramsSerializer: p => qs.stringify(p, { arrayFormat: 'repeat' }),
    });

    return {
      pageInfo: data.pageInfo,
      items: data.items,
    };
  }

  async getChildProfile(id: number): Promise<ChildProfile> {
    const { data } = await this.httpClient.get<ChildProfile>(
      `${this.httpClient.defaults.baseURL}/children/${id}`,
    );

    return data;
  }

  async addChild(body: IAddChild, clientId: number) {
    const { data } = await this.httpClient.post(`${this.httpClient.defaults.baseURL}/children`, {
      ...body,
      clientPrimaryAccountId: clientId,
    });

    return data;
  }

  async editChild(id: number, body: IAddChild) {
    const { data } = await this.httpClient.put(
      `${this.httpClient.defaults.baseURL}/children/${id}`,
      body,
    );

    return data;
  }

  private getPatientListParams() {
    const queries = getQueries();

    return {
      Page: queries.pagination?.page,
      PageSize: queries.pagination?.pageSize,
      SearchKeyword: queries.filtering?.SearchKeyword || undefined,
      [PatientsRequestFilters.Countries]:
        queries.filtering?.[PatientsRequestFilters.Countries] || undefined,
      [PatientsRequestFilters.Genders]:
        queries.filtering?.[PatientsRequestFilters.Genders] || undefined,
      [PatientsRequestFilters.AssessmentStatuses]:
        queries.filtering?.[PatientsRequestFilters.AssessmentStatuses] || undefined,
      [PatientsRequestFilters.TreatmentPlanStatuses]:
        queries.filtering?.[PatientsRequestFilters.TreatmentPlanStatuses] || undefined,
      [PatientsRequestFilters.DegreeOfNeurologicalFunctions]:
        queries.filtering?.[PatientsRequestFilters.DegreeOfNeurologicalFunctions] || undefined,
      [PatientsRequestFilters.AssignedClinicians]:
        queries.filtering?.[PatientsRequestFilters.AssignedClinicians] || undefined,
      'Sort.Field': queries.sorting?.field,
      'Sort.Type': queries.sorting?.type,
    };
  }
}
